import * as echarts from 'echarts/core';
import StaticEChart from '@/components/common/StaticEChart';
import type { EChartsOption } from 'echarts';
import type { StaticEChartHandle } from '@/components/common/StaticEChart';
import { breakpointsMediaQuery, getColor, useMediaQuery } from '@/helpers/utils';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import numberHumanizer from '@/helpers/numberHumanizer';
import timeStampToDate from '@/helpers/timeStampToDate';
import { useTooltipHide } from '@/hooks/useTooltipHide';
import { useRef } from 'react';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

type RankVolumeSeries = {
  name: string;
  data: unknown[];
  itemStyle?: Record<string, unknown>;
};

type RankVolumeChartProps = {
  series: RankVolumeSeries[];
  dates: Array<string | number>;
  count: number;
  isFirstRow?: boolean;
  isLastRow?: boolean;
};

type VolumeTooltipParam = {
  name?: string | number;
  marker?: string;
  value?: string | number | null;
};

const RankVolumeChart = ({
  series,
  dates,
  count
}: RankVolumeChartProps) => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const chartRef = useRef<StaticEChartHandle | null>(null);
  useTooltipHide(chartRef);
  const chart_series = series.map(item => {
    return {
      animation: false,
      type: 'line',
      name: item.name,
      data: item.data,
      stack: 'one',
      itemStyle: item.itemStyle,
      showSymbol: true,
      symbol: 'circle',
      smooth: false,
      emphasis: {
        itemStyle: {
          color: getColor('navbar-vertical-default-link-active-color')
        },
        lineStyle: {
          color: getColor('navbar-vertical-default-link-active-color')
        }
      }
    };
  });

  /*کانفیگ های مربوط به نمودار */
  const options = ({
    textStyle: {
      fontFamily: 'dana'
    },
    color: [getColor('navbar-vertical-default-link-active-color')],
    tooltip: {
      triggerOn: isMdUp ? 'mousemove' : 'click',
      trigger: 'axis',
      appendTo: document.body,
      padding: [7, 10],
      borderColor: getColor('gray-300'),
      backgroundColor: getColor('gray-100'),
      textStyle: { color: getColor('gray-1100') },
      borderWidth: 1,
      z: 9999999,
      formatter: (params: VolumeTooltipParam[]) => {
        return `
          <div>
          <div style="font-family: dana !important;gap:5px">
          <div class="d-flex gap-1 fs--2">
        تاریخ  <span style="direction:rtl !important;">${timeStampToDate(Number(params[0]?.name))?.slice(8)?.toPersianDigit() || 'نامشخص'}:</span>
          </div>
          <div style="direction:rtl;" class="d-flex flex-column">
          <span class="d-flex justify-content-between" style="direction:ltr;margin:0;padding:0"><span class="fw-bolder">${params[0]?.marker} رتبه: ${numberHumanizer(params[0].value)?.toString()?.toPersianDigit() || 'نامشخص'}</span>
            </div>
          </div>`;
      },
      transitionDuration: 0,
      axisPointer: {
        link: [
          {
            xAxisIndex: [0, 1]
          }
        ]
      }
    },
    xAxis: {
      type: 'category',
      data: dates.slice(count * -1),
      axisLine: {
        show: false,
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      inverse: true,
      axisLabel: {
        show: false,
        color: getColor('gray-800'),
        margin: 5
      },
      splitLine: {
        show: false,
        lineStyle: {
          color: getColor('gray-200')
        }
      },
      axisTick: { show: false },
      axisLine: { show: false },
      min: 0
    },
    series: chart_series,
    grid: { right: '0%', left: '0%', bottom: '20%', top: '20%' }
  } as unknown) as EChartsOption;

  return (
    <StaticEChart
      ref={chartRef}
      echarts={echarts}
      option={options}
      style={{ height: '100%' }}
    />
  );
};
export default RankVolumeChart;
